import { StyleSheet, css } from 'aphrodite'
import { useEffect, useState } from 'react'
import { projectService } from '../../services/projectService'
import { userService } from '../../services/userService'
import { type Attachment, PaymentType, type Project } from '../../types/project'
import type { Contact, User } from '../../types/user'
import type { PartySnapshot, WorkOrder } from '../../types/workorder'
import {
  getAssignedPersonName,
  getBrokerNames,
  getContractorNames,
  getDisplayNameByUserId,
} from '../../utils/relationshipHelpers'
import ContactInfoDisplay from '../ContactInfoDisplay'
import NoteAttachments from '../NoteAttachments'

// Helper component to display snapshot info
const SnapshotDisplay: React.FC<{ snapshot: PartySnapshot }> = ({
  snapshot,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
    <span
      style={{
        color: '#111827',
        fontWeight: 500,
        fontSize: '13px',
        lineHeight: '1.4',
      }}
    >
      {snapshot.first_name} {snapshot.last_name}
    </span>
    {snapshot.email && (
      <span style={{ color: '#6B7280', fontSize: '12px', lineHeight: '1.4' }}>
        {snapshot.email}
      </span>
    )}
    {snapshot.phone && (
      <span style={{ color: '#6B7280', fontSize: '12px', lineHeight: '1.4' }}>
        {snapshot.phone}
      </span>
    )}
    {snapshot.company_name && (
      <span style={{ color: '#6B7280', fontSize: '12px', lineHeight: '1.4' }}>
        {snapshot.company_name}
      </span>
    )}
  </div>
)

interface WorkOrderConfirmationProps {
  data: Partial<WorkOrder>
  attachments?: Attachment[]
  pendingAttachments?: File[]
}

const TRUCK_TYPE_LABELS: Record<string, string> = {
  standard: 'Standard Dump Truck',
  super: 'Super Dump Truck',
  winter: 'Winter Weather Dump Truck',
  transfer: 'Transfer Dump Truck',
  side: 'Side Dump Truck',
  haul: 'Haul Dump Truck',
  semi_end: 'Semi-Truck Trailer End Dump Truck',
  semi_bottom: 'Semi-Truck Trailer Bottom Dump Truck',
}

const WorkOrderConfirmation: React.FC<WorkOrderConfirmationProps> = ({
  data,
  attachments = [],
  pendingAttachments = [],
}) => {
  const [contacts, setContacts] = useState<Contact[]>([])
  const [project, setProject] = useState<Project | null>(null)
  // Legacy display name state removed - new API uses subhaulers[] and dispatchers[] arrays
  // No longer needed - project data now includes full user objects from backend

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contactsData, projectData] = await Promise.all([
          userService.getContacts(),
          // Fetch project data if data.project is just an ID
          typeof data.project === 'number'
            ? projectService.getProject(data.project)
            : Promise.resolve(data.project),
        ])

        setContacts(contactsData)
        setProject(projectData || null)
      } catch (error) {
        console.error('Failed to fetch data:', error)
      }
    }

    fetchData()
  }, [data.project])

  // No longer needed - project data now includes full user objects from backend

  // Fetch display names for subhauler and dispatcher (only if not already available)
  // Note: Legacy display name fetching removed - new API uses subhaulers[] and dispatchers[] arrays

  // Legacy fallback for contact display
  const getContactName = (contactId: number) => {
    const contact = contacts.find((c) => c.id === contactId)
    return contact?.contact_object?.name || 'None'
  }

  // Helper function to format contact info with email and phone
  const formatContactInfo = (user: User) => {
    const fullName = `${user.first_name} ${user.last_name}`.trim()
    const parts: string[] = []

    if (fullName) {
      parts.push(fullName)
    }

    if (user.email) {
      parts.push(`(${user.email})`)
    }

    if (user.phone_number) {
      parts.push(`- ${user.phone_number}`)
    }

    return parts.join(' ') || 'None'
  }

  // Enhanced broker display with fallback
  const displayBrokers = () => {
    // Use project from state if available, otherwise use data.project directly
    const projectData =
      project || (typeof data.project === 'object' ? data.project : null)
    if (!projectData) return 'None'

    // First priority: Use the project.brokers data directly (now contains full user objects from backend)
    if (projectData.brokers && projectData.brokers.length > 0) {
      return projectData.brokers
        .map((user) => formatContactInfo(user))
        .join(', ')
    }

    // Fallback to preusers if available
    if (projectData.broker_preusers && projectData.broker_preusers.length > 0) {
      return getBrokerNames(
        projectData.brokers,
        projectData.broker_preusers,
        contacts.filter((c) => projectData.broker_contacts?.includes(c.id)),
      )
    }

    // Final fallback to legacy contact display
    return (
      projectData.broker_contacts
        ?.map((id) => {
          const contact = contacts.find((c) => c.id === id)
          return (
            contact?.contact_object?.name ||
            contact?.contact_object?.email ||
            'None'
          )
        })
        .join(', ') || 'None'
    )
  }

  // Enhanced contractor display with fallback
  const displayContractors = () => {
    // Use project from state if available, otherwise use data.project directly
    const projectData =
      project || (typeof data.project === 'object' ? data.project : null)
    if (!projectData) return 'None'

    // First priority: Use the project.contractors data directly (now contains full user objects from backend)
    if (projectData.contractors && projectData.contractors.length > 0) {
      return projectData.contractors
        .map((user) => formatContactInfo(user))
        .join(', ')
    }

    // Fallback to preusers if available
    if (
      projectData.contractor_preusers &&
      projectData.contractor_preusers.length > 0
    ) {
      return getContractorNames(
        projectData.contractors,
        projectData.contractor_preusers,
        contacts.filter((c) => projectData.contractor_contacts?.includes(c.id)),
      )
    }

    // Final fallback to legacy contact display
    return (
      projectData.contractor_contacts
        ?.map((id) => {
          const contact = contacts.find((c) => c.id === id)
          return (
            contact?.contact_object?.name ||
            contact?.contact_object?.email ||
            'None'
          )
        })
        .join(', ') || 'None'
    )
  }

  // Enhanced creator display (now uses work order's created_by field from backend)
  const displayCreator = () => {
    if (!data) return 'None'

    // Use the data.created_by data directly (now contains full user object from backend)
    if (data.created_by && typeof data.created_by === 'object') {
      return formatContactInfo(data.created_by)
    }

    return 'None'
  }

  // Enhanced subhaulers display with fallback
  const displaySubhaulers = () => {
    // First priority: Use the stored subhaulers data from form selection
    if (data.subhaulers && data.subhaulers.length > 0) {
      return data.subhaulers.map((user) => formatContactInfo(user)).join(', ')
    }

    // Legacy fallback removed - new API uses subhaulers[] array
    return 'None'
  }

  // Enhanced dispatchers display with fallback
  const displayDispatchers = () => {
    // First priority: Use the stored dispatchers data from form selection
    if (data.dispatchers && data.dispatchers.length > 0) {
      return data.dispatchers.map((user) => formatContactInfo(user)).join(', ')
    }

    // Legacy fallback removed - new API uses dispatchers[] array
    return 'None'
  }

  return (
    <div className={css(styles.container)}>
      <h3 className={css(styles.sectionTitle)}>
        Dispatch - Information - Pending
      </h3>

      <div className={css(styles.section)}>
        <h4 className={css(styles.subTitle)}>Contact Information</h4>
        <div className={css(styles.grid)}>
          {/* Contractor - prefer snapshot, fallback to FK */}
          {(data.contractor_snapshot ||
            (project?.contractors && project.contractors.length > 0)) && (
            <div className={css(styles.field)}>
              <span className={css(styles.label)}>Contractor</span>
              {data.contractor_snapshot ? (
                <SnapshotDisplay snapshot={data.contractor_snapshot} />
              ) : (
                project?.contractors?.[0] && (
                  <ContactInfoDisplay
                    user={project.contractors[0]}
                    variant="inline"
                  />
                )
              )}
            </div>
          )}
          {/* Broker - prefer snapshot, fallback to FK */}
          {(data.broker_snapshot ||
            (project?.brokers && project.brokers.length > 0)) && (
            <div className={css(styles.field)}>
              <span className={css(styles.label)}>
                {data.created_by_role === 'BROKER'
                  ? 'Sent from Broker'
                  : 'Broker'}
              </span>
              {data.broker_snapshot ? (
                <SnapshotDisplay snapshot={data.broker_snapshot} />
              ) : (
                project?.brokers?.[0] && (
                  <ContactInfoDisplay
                    user={project.brokers[0]}
                    variant="inline"
                  />
                )
              )}
            </div>
          )}
          {/* Third-Party Broker - from project.third_party_brokers */}
          {project?.third_party_brokers &&
            project.third_party_brokers.length > 0 && (
              <div className={css(styles.field)}>
                <span className={css(styles.label)}>Third-Party Broker</span>
                <ContactInfoDisplay
                  user={project.third_party_brokers[0]}
                  variant="inline"
                />
              </div>
            )}
          {/* Third-Party Contractor - from project.third_party_contractors */}
          {project?.third_party_contractors &&
            project.third_party_contractors.length > 0 && (
              <div className={css(styles.field)}>
                <span className={css(styles.label)}>
                  Third-Party Contractor
                </span>
                <ContactInfoDisplay
                  user={project.third_party_contractors[0]}
                  variant="inline"
                />
              </div>
            )}
          {/* Sub Hauler - prefer snapshot, fallback to FK */}
          {(data.sub_hauler_snapshot ||
            (data.subhaulers && data.subhaulers.length > 0) ||
            (project?.sub_haulers && project.sub_haulers.length > 0)) && (
            <div className={css(styles.field)}>
              <span className={css(styles.label)}>Sub Hauler</span>
              {data.sub_hauler_snapshot ? (
                <SnapshotDisplay snapshot={data.sub_hauler_snapshot} />
              ) : (
                <ContactInfoDisplay
                  user={data.subhaulers?.[0] || project?.sub_haulers?.[0]}
                  variant="inline"
                />
              )}
            </div>
          )}
          {/* Dispatcher - from data.dispatchers (with fallback to created_by snapshot) */}
          {(data.dispatchers && data.dispatchers.length > 0) ||
          (data.created_by_first_name && data.created_by_last_name) ? (
            <div className={css(styles.field)}>
              <span className={css(styles.label)}>Dispatcher</span>
              {data.dispatchers && data.dispatchers.length > 0 ? (
                <ContactInfoDisplay
                  user={data.dispatchers[0]}
                  variant="inline"
                />
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                  }}
                >
                  <span
                    style={{
                      color: '#111827',
                      fontWeight: 500,
                      fontSize: '13px',
                      lineHeight: '1.4',
                    }}
                  >
                    {data.created_by_first_name} {data.created_by_last_name}
                  </span>
                  {data.created_by_email && (
                    <span
                      style={{
                        color: '#6B7280',
                        fontSize: '12px',
                        lineHeight: '1.4',
                      }}
                    >
                      {data.created_by_email}
                    </span>
                  )}
                  {data.created_by_company_name && (
                    <span
                      style={{
                        color: '#6B7280',
                        fontSize: '12px',
                        lineHeight: '1.4',
                      }}
                    >
                      {data.created_by_company_name}
                    </span>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <hr className={css(styles.divider)} />

      <div className={css(styles.section)}>
        <h4 className={css(styles.subTitle)}>Locations</h4>
        <div className={css(styles.locationGrid)}>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Pick Up Location</span>
            <span className={css(styles.value)}>
              {data.pickup_address || 'None'}
            </span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Drop Off Location</span>
            <span className={css(styles.value)}>
              {data.dropoff_address || 'None'}
            </span>
          </div>
        </div>
      </div>

      <hr className={css(styles.divider)} />

      <div className={css(styles.section)}>
        <h4 className={css(styles.subTitle)}>Job Details</h4>
        <div className={css(styles.grid)}>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Project ID</span>
            <span className={css(styles.value)}>{project?.label || '-'}</span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Dispatch ID</span>
            <span className={css(styles.value)}>{data.label || '-'}</span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Material</span>
            <span className={css(styles.value)}>{data.material || 'None'}</span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Start Date & Time</span>
            <span className={css(styles.value)}>
              {data.starting_date
                ? new Date(data.starting_date).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                  })
                : 'None'}
            </span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Number of Trucks</span>
            <span className={css(styles.value)}>
              {data.trucks_number || 'None'}
            </span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Truck Types</span>
            <span className={css(styles.value)}>
              {data.truck_types
                ?.map((type) => TRUCK_TYPE_LABELS[type])
                .join(', ') || 'None'}
            </span>
          </div>
          {data.notes && (
            <div className={css(styles.field, styles.fullWidth)}>
              <span className={css(styles.label)}>Notes</span>
              <span className={css(styles.value)}>{data.notes}</span>
            </div>
          )}
          {(attachments.length > 0 || pendingAttachments.length > 0) && (
            <div className={css(styles.field, styles.fullWidth)}>
              <span className={css(styles.label)}>Attachments</span>
              <NoteAttachments
                attachments={attachments}
                pendingFiles={pendingAttachments}
                onPendingFilesChange={() => {}}
                readOnly={true}
              />
            </div>
          )}
        </div>
      </div>

      <hr className={css(styles.divider)} />

      <div className={css(styles.section)}>
        <h4 className={css(styles.subTitle)}>Payment Information</h4>
        <div className={css(styles.grid)}>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Payment Type</span>
            <span className={css(styles.value)}>
              {data.payment_type === PaymentType.MILESTONE
                ? 'Milestone Payment'
                : data.payment_type === PaymentType.PROGRESS
                  ? 'Progress Payment'
                  : 'Rate Type'}
            </span>
          </div>
          {(!data.payment_type ||
            data.payment_type === PaymentType.RATE_TYPE) && (
            <>
              <div className={css(styles.field)}>
                <span className={css(styles.label)}>Rate Type</span>
                <span className={css(styles.value)}>
                  {data.rate_type || 'None'}
                </span>
              </div>
              <div className={css(styles.field)}>
                <span className={css(styles.label)}>Pay Rate</span>
                <span className={css(styles.value)}>
                  {data.pay_rate ? `$${data.pay_rate}` : 'None'}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Payment Schedule Display for Milestone/Progress */}
        {data.payment_type &&
          data.payment_type !== PaymentType.RATE_TYPE &&
          data.payment_schedule &&
          data.payment_schedule.length > 0 && (
            <div className={css(styles.scheduleSection)}>
              <h5 className={css(styles.scheduleTitle)}>
                {data.payment_type === PaymentType.MILESTONE
                  ? 'Milestone Schedule'
                  : 'Progress Payment Schedule'}
              </h5>
              <div className={css(styles.scheduleTable)}>
                <div className={css(styles.scheduleHeader)}>
                  <span
                    className={css(
                      styles.scheduleHeaderCell,
                      styles.deliverableCol,
                    )}
                  >
                    Deliverable
                  </span>
                  <span
                    className={css(styles.scheduleHeaderCell, styles.dateCol)}
                  >
                    Due Date
                  </span>
                  <span
                    className={css(styles.scheduleHeaderCell, styles.amountCol)}
                  >
                    Pay
                  </span>
                </div>
                {data.payment_schedule.map((entry, index) => (
                  <div
                    key={`schedule-${entry.deliverable}-${entry.due_date}-${index}`}
                    className={css(styles.scheduleRow)}
                  >
                    <span
                      className={css(
                        styles.scheduleCell,
                        styles.deliverableCol,
                      )}
                    >
                      {entry.deliverable || '-'}
                    </span>
                    <span className={css(styles.scheduleCell, styles.dateCol)}>
                      {entry.due_date
                        ? new Date(entry.due_date).toLocaleDateString('en-US')
                        : '-'}
                    </span>
                    <span
                      className={css(styles.scheduleCell, styles.amountCol)}
                    >
                      ${Number(entry.pay_amount || 0).toFixed(2)}
                    </span>
                  </div>
                ))}
                <div className={css(styles.scheduleTotals)}>
                  <span className={css(styles.scheduleTotalLabel)}>
                    Total Scheduled Payment:
                  </span>
                  <span className={css(styles.scheduleTotalAmount)}>
                    ${Number(data.total_scheduled_payment || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    padding: '20px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '24px',
  },
  subTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '16px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  label: {
    color: '#6B7280',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    lineHeight: '1.4',
  },
  value: {
    color: '#111827',
    fontWeight: 500,
    fontSize: '13px',
    lineHeight: '1.4',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #E5E7EB',
    margin: '24px 0',
  },
  locationGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '16px',
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  scheduleSection: {
    marginTop: '20px',
  },
  scheduleTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '12px',
  },
  scheduleTable: {
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    padding: '12px',
    border: '1px solid #E5E7EB',
  },
  scheduleHeader: {
    display: 'flex',
    borderBottom: '1px solid #E5E7EB',
    paddingBottom: '8px',
    marginBottom: '8px',
  },
  scheduleHeaderCell: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
  },
  scheduleRow: {
    display: 'flex',
    padding: '8px 0',
    borderBottom: '1px solid #E5E7EB',
  },
  scheduleCell: {
    fontSize: '13px',
    color: '#111827',
  },
  deliverableCol: {
    flex: 2,
    paddingRight: '12px',
  },
  dateCol: {
    flex: 1,
    paddingRight: '12px',
  },
  amountCol: {
    flex: 1,
    textAlign: 'right' as const,
  },
  scheduleTotals: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 0 0',
    borderTop: '2px solid #374151',
    marginTop: '4px',
  },
  scheduleTotalLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#374151',
  },
  scheduleTotalAmount: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#111827',
  },
})

export default WorkOrderConfirmation
